
import { doc, setDoc, getDoc, updateDoc, deleteField } from "firebase/firestore";
import { db } from "@/firebase";
import md5 from "md5"; // tu dois avoir `npm install md5`

/**
 * Ajoute une recette dans le document `${userId}-recipes`
 * Chaque recette est stockée dans une map, indexée par un id md5.
 */
export async function addRecipeForUser(userId, recipeData) {
  const docRef = doc(db, "Recipes", `${userId}-recipes`);

  try {
    const docSnap = await getDoc(docRef);
    const existingData = docSnap.exists() ? docSnap.data() : {};

    // On génère un id md5 pour la recette
    const recipeId = md5(
      recipeData.title + Date.now().toString() + Math.random().toString()
    );

    // Mise à jour de la map
    const updatedData = {
      ...existingData,
      [recipeId]: recipeData,
    };

    await setDoc(docRef, updatedData);

    return { id: recipeId };
  } catch (error) {
    console.error("Erreur lors de l'ajout de la recette :", error);
    throw error;
  }
}

/**
 * Supprime un ingrédient de la collection d'un utilisateur.
 * @param {string} userId - L'ID de l'utilisateur.
 * @param {string} recipeDocId - L'ID de la recette à supprimer.
 */
export async function deleteRecipeFromUserCollection(userId, recipeDocId) {
  const docRef = doc(db, "Recipes", `${userId}-recipes`);

  try {
    // use an atomic update to delete the specific field to avoid races and large-document replacement
    await updateDoc(docRef, {
      [recipeDocId]: deleteField(),
    });
  } catch (error) {
    // If the doc doesn't exist or field absent, updateDoc will throw — fallback to read/replace for compatibility
    if (error.code === 'not-found' || /No document to update/.test(String(error))) {
      // try safe read-and-replace as fallback
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        console.warn("Aucun document trouvé.");
        return;
      }
      const data = docSnap.data();
      if (!Object.prototype.hasOwnProperty.call(data, recipeDocId)) {
        console.warn("La recette n'existe pas dans la collection.");
        return;
      }
      delete data[recipeDocId];
      await setDoc(docRef, data);
      return;
    }

    console.error("Erreur lors de la suppression de la recette :", error);
    throw error;
  }
}

/**
 * Met à jour un ingrédient en le passant en favori (favoris = true).
 * @param {string} userId - L'ID de l'utilisateur.
 * @param {int} recipeDocId - L'ID de la recette.
 */
export async function setRecipeAsFavorite(userId, recipeDocId, isFavorite = true) {
  const docRef = doc(db, "Recipes", `${userId}-recipes`);

  try {
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;

    const data = docSnap.data();
    if (!Object.prototype.hasOwnProperty.call(data, recipeDocId)) return;

    const updatedRecipe = {
      ...data[recipeDocId],
      favorite: isFavorite, // ← mise à jour ici
    };

    await setDoc(docRef, {
      ...data,
      [recipeDocId]: updatedRecipe,
    });

  } catch (error) {
    console.error("Erreur favori :", error);
    throw error;
  }
}

/**
 * Récupère les recettes d'un utilisateur.
 * @param {string} userId - L'ID de l'utilisateur.
 * @returns {Promise<Object>} Une map de recettes ou un objet vide.
 */
export async function getUserRecipes(userId) {
  const docRef = doc(db, "Recipes", `${userId}-recipes`);

  try {
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return {};
    }

    const data = docSnap.data();
    return data;
  } catch (error) {
    console.error("Erreur lors de la récupération des recettes :", error);
    throw error;
  }
}

export async function updateRecipeForUser(userId, recipeId, recipeData) {
  const docRef = doc(db, "Recipes", `${userId}-recipes`);
  try {
    const snap = await getDoc(docRef);
    const existing = snap.exists() ? snap.data() : {};
    if (!existing[recipeId]) {
      console.warn("Recette introuvable, création à la place.");
    }
    await setDoc(docRef, {
      ...existing,
      [recipeId]: { ...recipeData, updatedAt: Date.now() },
    });
    return { id: recipeId };
  } catch (e) {
    console.error("Erreur update recette :", e);
    throw e;
  }
}


/**
 * Renvoie la recette d'un utilisateur par son id (ou null si absente).
 * Stockage: collection "Recipes", doc "${userId}-recipes" (map { [recipeId]: recipe })
 */
export async function getUserRecipeById(userId, recipeId) {
  if (!userId || !recipeId) return null;

  const docRef = doc(db, "Recipes", `${userId}-recipes`);
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;

    const data = snap.data() || {};
    const recipe = data[recipeId] || null;
    // Option: renvoyer aussi l'id
    return recipe ? { id: recipeId, ...recipe } : null;
  } catch (e) {
    console.error("Erreur getUserRecipeById:", e);
    throw e;
  }
}

/**
 * Récupère les infos d'une recette grâce à un id utilisateur et un id recette.
 * @param {string} userId - L'ID de l'utilisateur.
 * @param {string} recipeDocId - L'ID de la recette.
 */
export async function getRecipeDetails(userId, recipeDocId) {
  const docRef = doc(db, "Recipes", `${userId}-recipes`);

  try {
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    if (!Object.prototype.hasOwnProperty.call(data, recipeDocId)) {
      return null;
    }

    return data[recipeDocId];
  } catch (error) {
    console.error("Erreur lors de la récupération des détails de la recette :", error);
    throw error;
  }
}


